import React, { useState, useEffect } from 'react';
import { updateUserInfo, resetUserPassword } from '../adapters/authentication';

function Field({ label, type = 'text', value, onChange, placeholder }: {
  label: string; type?: string; value: string;
  onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: 'var(--fg-muted)',
      }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '10px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-soft)',
          borderRadius: 8, fontSize: 13,
          color: 'var(--fg-strong)',
          fontFamily: 'inherit', outline: 'none',
          transition: 'border-color 0.15s',
          boxSizing: 'border-box', width: '100%',
        }}
      />
    </div>
  );
}

function SaveBtn({ onClick, loading, label, disabled }: {
  onClick: () => void; loading: boolean; label: string; disabled?: boolean;
}) {
  const isDisabled = loading || disabled;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      style={{
        alignSelf: 'flex-start',
        padding: '9px 20px',
        background: isDisabled ? 'var(--bg-surface-3)' : 'var(--brand-navy-800)',
        color: isDisabled ? 'var(--fg-faint)' : '#fff',
        border: 'none', borderRadius: 8,
        fontSize: 12.5, fontWeight: 700,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        transition: 'background 0.15s',
        marginTop: 4,
      }}
    >
      {loading ? 'Saving…' : label}
    </button>
  );
}

function Alert({ type, children }: { type: 'error' | 'success'; children: React.ReactNode }) {
  const isError = type === 'error';
  return (
    <div style={{
      padding: '9px 12px', borderRadius: 8, fontSize: 12.5,
      background: isError ? 'rgba(220,38,38,0.08)' : 'rgba(0,200,150,0.08)',
      border: `1px solid ${isError ? 'rgba(220,38,38,0.25)' : 'rgba(0,200,150,0.25)'}`,
      color: isError ? '#dc2626' : 'var(--brand-green-500)',
    }}>{children}</div>
  );
}

export function SettingsPage({ user, onUserUpdate }: {
  user?: { first_name?: string; last_name?: string; username?: string; email?: string };
  onUserUpdate?: (u: { first_name: string; last_name: string; username: string; email: string }) => void;
}) {
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [infoStatus, setInfoStatus] = useState<{ type: 'error' | 'success'; msg: string } | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwStatus, setPwStatus] = useState<{ type: 'error' | 'success'; msg: string } | null>(null);
  const [pwLoading, setPwLoading] = useState(false);

  // Sync prop → state if user loads after mount
  useEffect(() => {
    if (user?.first_name !== undefined) setFirstName(user.first_name);
    if (user?.last_name !== undefined) setLastName(user.last_name);
    if (user?.username) setUsername(user.username);
    if (user?.email) setEmail(user.email);
  }, [user?.first_name, user?.last_name, user?.username, user?.email]);

  const handleSaveInfo = async () => {
    setInfoStatus(null);
    setInfoLoading(true);
    const { data, error } = await updateUserInfo(username, email, firstName, lastName);
    setInfoLoading(false);
    if (error) {
      setInfoStatus({ type: 'error', msg: error.message || 'Failed to update account info.' });
    } else {
      const saved = {
        first_name: data?.first_name ?? firstName,
        last_name: data?.last_name ?? lastName,
        username: data?.username ?? username,
        email: data?.email ?? email,
      };
      const name = [saved.first_name, saved.last_name].filter(Boolean).join(' ') || saved.username;
      setInfoStatus({ type: 'success', msg: `Saved — welcome, ${name}!` });
      onUserUpdate?.(saved);
    }
  };

  const handleChangePassword = async () => {
    setPwStatus(null);
    if (newPw !== confirmPw) { setPwStatus({ type: 'error', msg: 'Passwords do not match.' }); return; }
    if (newPw.length < 8) { setPwStatus({ type: 'error', msg: 'New password must be at least 8 characters.' }); return; }
    setPwLoading(true);
    const { data, error } = await resetUserPassword(currentPw, newPw);
    setPwLoading(false);
    if (error) {
      setPwStatus({ type: 'error', msg: error.message || 'Failed to update password.' });
    } else {
      setPwStatus({ type: 'success', msg: data?.detail ?? 'Password updated successfully.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    }
  };

  const card: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 12,
    padding: '24px 28px',
    display: 'flex', flexDirection: 'column', gap: 16,
  };

  return (
    <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Account info */}
      <div style={card}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 2 }}>
            Account information
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            Update your name, username, and email address.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="First name" value={firstName} onChange={setFirstName} />
          <Field label="Last name" value={lastName} onChange={setLastName} />
        </div>
        <Field label="Username" value={username} onChange={setUsername} />
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" />

        {infoStatus && <Alert type={infoStatus.type}>{infoStatus.msg}</Alert>}
        <SaveBtn onClick={handleSaveInfo} loading={infoLoading} label="Save changes" />
      </div>

      {/* Change password */}
      <div style={card}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 2 }}>
            Change password
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            You'll be prompted to sign in again after changing your password.
          </div>
        </div>

        <Field label="Current password" type="password" value={currentPw} onChange={setCurrentPw} />
        <Field label="New password" type="password" value={newPw} onChange={setNewPw} placeholder="At least 8 characters" />
        <Field label="Confirm new password" type="password" value={confirmPw} onChange={setConfirmPw} />

        {pwStatus && <Alert type={pwStatus.type}>{pwStatus.msg}</Alert>}
        <SaveBtn onClick={handleChangePassword} loading={pwLoading} label="Update password" disabled={!currentPw || !newPw || !confirmPw} />
      </div>

    </div>
  );
}
